// PricingCards.jsx
import React from 'react';
import styles from './packs.module.css';
import pack from '../../assets/img/pack1.png'
import pro from '../../assets/img/pack2.png'

// Custom Check icon component instead of using lucide-react
const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

// Custom Diamond icon component

const PricingCard = ({ tier, maxSteps, corrections, singlePrice, doublePrice, isSelected, isPro, onSelect }) => {
  const getCardClass = () => {
    switch (tier) {
      case 'Lite': return styles.cardLite;
      case 'Essential': return styles.cardEssential;
      case 'Smart': return styles.cardSmart;
      case 'Pro': return styles.cardPro;
      case 'Pro+': return styles.cardProPlus;
      default: return '';
    }
  };

  // Get background number based on tier
  const getBackgroundNumber = () => {
    switch (tier) {
      case 'Lite': return '7';
      case 'Essential': return '12';
      case 'Smart': return '24';
      case 'Pro': return '∞';
      case 'Pro+': return '∞';
      default: return '';
    }
  };

  // Use different image based on whether it's a Pro pack or not
  const packImage = tier === 'Pro' || tier === 'Pro+' ? pro : pack;
  
  // Add a class for Pro tier names to style them differently
  const tierNameClass = tier === 'Pro' || tier === 'Pro+' ? styles.proTierName : styles.tierName;
  const dividerNameClass = tier === 'Pro' || tier === 'Pro+' ? styles.dividerPro : styles.divider;
  
  // Determine if this is a Pro tier
  const isPack = tier === 'Pro' || tier === 'Pro+';
  
  return (
    <div className={`${styles.card} ${getCardClass()} ${isSelected ? styles.selected : ''}`}>
      {/* Background number as a separate div */}
      <div className={styles.backgroundNumber}>
        {getBackgroundNumber()}
      </div>
      
      {isSelected && (
        <div className={styles.selectedBadge}>
          <CheckIcon />
        </div>
      )}
      
      <div className={styles.cardHeader}>
        <img src={packImage} alt={tier} className={styles.packImage} />
        <h2 className={tierNameClass}>{tier}</h2>
      </div>
      <div className={dividerNameClass}></div>
      
      <div className={styles.feature}>
        <p className={styles.featureLabel}>Nombre maximal d'étapes par arcade</p>
        <p className={styles.featureValue}>{maxSteps}</p>
      </div>
      
      <div className={styles.feature}>
        <p className={styles.featureLabel}>Nombre de corrections incluses</p>
        <p className={styles.featureValue}>{corrections}</p>
      </div>
      
      <div className={dividerNameClass}></div>
      
      {/* Modified pricing section */}
      <div className={styles.pricingContainer}>
        {!isPack ? (
          <>
            <div className={styles.pricing}>
              <p className={styles.price}>{singlePrice} TND</p>
              <p className={styles.priceLabel}>Arcade Unique</p>
            </div>
            <div className={styles.pricingDouble}>
              <p className={styles.price}>{doublePrice} TND</p>
              <p className={styles.priceLabel}>Deux Arcades</p>
            </div>
          </>
        ) : (
          // For Pro and Pro+ packs
          <div className={styles.pricingSingle}>
            <div className={styles.spacer}></div>
            <div className={styles.pricingDouble}>
              <p className={styles.pricePro}>{doublePrice} TND</p>
              <p className={styles.priceLabel}>Deux Arcades</p>
            </div>
          </div>
        )}
      </div>
      
      <button 
        className={`${styles.selectButton} ${getCardClass()}`}
        onClick={() => onSelect(tier)}
      >
        Sélectionner
      </button>
    </div>
  );
};

const Packs = ({ onSelectPack, selectedPack, user }) => {
  // Define all packs
  const allPacks = [
    {
      tier: "Lite",
      maxSteps: "7 étapes",
      corrections: "1 correction",
      singlePrice: "850",
      doublePrice: "1300"
    },
    {
      tier: "Essential",
      maxSteps: "12 étapes",
      corrections: "1 correction",
      singlePrice: "1150",
      doublePrice: "1950"
    },
    {
      tier: "Smart",
      maxSteps: "24 étapes",
      corrections: "2 correction",
      singlePrice: "2100",
      doublePrice: "3150"
    },
    {
      tier: "Pro",
      maxSteps: "36 étapes",
      corrections: "3 correction",
      singlePrice: "4500",
      doublePrice: "4500",
      isPro: true
    },
    {
      tier: "Pro+",
      maxSteps: "ILLIMITÉ *",
      corrections: "ILLIMITÉ *",
      singlePrice: "5000",
      doublePrice: "5000",
      isPro: true
    }
  ];
  // Determine if user is an orthodontist
  const isOrthodontist = React.useMemo(() => {
    if (!user) return false;
    
    // Check direct role property
    if (user.role === 'orthodontiste') return true;
    
    // Check if specialite contains 'ortho'
    if (user.specialite && user.specialite.toLowerCase().includes('ortho')) return true;
    
    // Check userData if it exists
    if (user.userData) {
      if (user.userData.role === 'orthodontiste') return true;
      if (user.userData.specialite && user.userData.specialite.toLowerCase().includes('ortho')) return true;
    }
    
    return false;
  }, [user]);

  // Filter packs based on user role
  const packsToShow = isOrthodontist ? allPacks : allPacks.slice(0, 3);

  return (
    <div className={styles.container}>
      {packsToShow.map((pack, index) => (
        <PricingCard
          key={pack.tier}
          tier={pack.tier}
          maxSteps={pack.maxSteps}
          corrections={pack.corrections}
          singlePrice={pack.singlePrice}
          doublePrice={pack.doublePrice}
          isSelected={selectedPack === pack.tier}
          onSelect={onSelectPack}
          isPro={pack.isPro}
        />
      ))}
    </div>
  );
};

export default Packs;